import { useEffect, useState } from "react";
import { cn } from "src/lib/utils";
import { useWindowSize } from "src/hooks/useWindowSize";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "src/v1/ui/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/v1/ui/table";
import useAxios from "src/hooks/useAxios";
import { commaizeNumber } from "../helpers/commaizeNumber";
import { isPrerender } from "src/common/prerender";
export interface SueDataResponse {
  사건번호: string;
  소가: string;
}

export function TableDemo() {
  const { axiosGetNoAuth } = useAxios();
  const { width } = useWindowSize();
  const [sueDataSource, setSueDataSource] = useState<SueDataResponse[] | null>(
    null,
  );
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;
  const totalPages = Math.ceil((sueDataSource?.length ?? 0) / itemsPerPage);
  const maxVisiblePages = width < 768 ? 4 : width > 1440 ? 4 : 3;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = sueDataSource?.slice(startIndex, endIndex) || [];
  const emptyRowsCount = itemsPerPage - currentPageData.length;

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    if (currentPage <= halfVisible + 1) {
      for (let i = 1; i <= maxVisiblePages - 1; i++) {
        pages.push(i);
      }
      pages.push("ellipsis");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - halfVisible) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = totalPages - maxVisiblePages + 2; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (
        let i = currentPage - halfVisible + 1;
        i <= currentPage + halfVisible - 1;
        i++
      ) {
        pages.push(i);
      }
      pages.push("ellipsis");
      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    // Prerender keeps the empty rows as the skeleton
    if (isPrerender()) return;
    const fetchData = async () => {
      const data = await axiosGetNoAuth("/api/landings/block5");
      data ? setSueDataSource(data) : setError(true);
    };
    fetchData();
  }, []);

  const handlePreviousPage = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    page: number,
  ) => {
    e.preventDefault();
    setCurrentPage(page);
  };

  return (
    <div className="w-full rounded-[24px] px-4 py-[10px] desktop:px-5 desktop:py-4 border border-[#4883D3]">
      <div className="flex flex-col gap-2 items-center">
        <Table>
          <TableHeader>
            <TableRow className="bg-white hover:bg-blue-50 text-[#4883D3] transition-colors">
              <TableHead className="p-0 text-heading-2-mb">
                진행 사건번호
              </TableHead>
              <TableHead className="p-0 text-right text-body-1">
                소송금액
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="border-none">
            {error && (
              <TableRow>
                <TableCell colSpan={2} className="text-center text-red-500">
                  서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
                </TableCell>
              </TableRow>
            )}
            {currentPageData.map((sueData, index) => (
              <TableRow
                key={`${sueData.사건번호}-${startIndex + index}`}
                className={cn(
                  "hover:bg-gray-100 transition-colors text-[#616161]",
                  index % 2 === 1 ? "bg-[#E6ECF2]" : "bg-[#616161]/38",
                )}
              >
                <TableCell className="py-0 px-1 text-label-1-mb desktop:py-2">
                  {sueData.사건번호}
                </TableCell>
                <TableCell className="text-right py-0 px-1 text-label-1-mb desktop:py-2">
                  {commaizeNumber(Number(sueData.소가))}원
                </TableCell>
              </TableRow>
            ))}
            {Array.from({ length: emptyRowsCount }).map((_, index) => (
              <TableRow
                key={`empty-${index}`}
                className={cn(
                  "transition-colors text-[#616161]",
                  (currentPageData.length + index) % 2 === 1
                    ? "bg-[#E6ECF2]"
                    : "bg-[#616161]/38",
                )}
              >
                <TableCell className="py-0 px-1 text-label-1-mb desktop:py-2">
                  &nbsp;
                </TableCell>
                <TableCell className="text-right py-0 px-1 text-label-1-mb desktop:py-2">
                  &nbsp;
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination className="max-w-[248px]">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={handlePreviousPage}
                className={cn(
                  "tablet:text-label-1",
                  currentPage === 1 && "pointer-events-none opacity-50",
                )}
              />
            </PaginationItem>
            {getVisiblePages().map((page, index) => (
              <PaginationItem key={index}>
                {page === "ellipsis" ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    href="#"
                    onClick={(e) => handlePageClick(e, page as number)}
                    isActive={currentPage === page}
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={handleNextPage}
                className={cn(
                  currentPage === totalPages &&
                    "pointer-events-none opacity-50",
                )}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}
